import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { environment } from '../../../environments/environment';
import { UserInformationSummary, getOrganizationFrom, getPhoneNumberFrom } from '../../interfaces/user-information-summary.interface';

// CPD Hours Interface (API Response)
export interface CpdHoursRecord {
  id: string;
  hoursStudied: string;
  studyDate: string;
  year: number;
  sessionTitle: string;
  notes: string;
  isVerified: boolean;
  paymentStatus: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  courseId: string;
  userCourseId: string | null;
  cpdCourseStatusId: number | null;
  cpdPaymentStatusId: number | null;
  paymentSlip?: string | null;        // หลักฐานการโอน
  codeReceipt?: string | null;        // รหัสใบเสร็จ (ว่าง = ยังไม่ออกใบเสร็จ)
  user: {
    id: string;
    email: string;
    cid: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    companyId: string | null;
    userInformation?: UserInformationSummary | null;
  };
  course: {
    id: string;
    title: string;
    description: string;
    instructor: string;
    duration: string;
    category: string;
    level: string;
    thumbnail: string | null;
    price: string;
    isActive: boolean;
    startDate: string | null;
    endDate: string | null;
    createdAt: string;
    updatedAt: string;
  };
  userCourse: any | null;
  cpdCourseStatus?: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
  cpdPaymentStatus?: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
}

// Filters Interface
export interface CpdFilters {
  searchTerm: string;
  email: string;
  cid: string;
  courseTitle: string;
  isVerified: string;
  /** '' = ทุกสถานะ ค่าอื่นคือ cpdCourseStatusId เป็นข้อความ */
  courseStatus: string;
  paymentStatus: string;
  yearFilter: string;
  dateFrom: string;
  dateTo: string;
}

/** แท็บกรองตามสถานะใบคำขอ พร้อมจำนวนรายการในแต่ละสถานะ */
interface CpdStatusTab {
  /** '' = แท็บรวมทุกสถานะ */
  id: number | '';
  name: string;
  /** ชื่อสีของ bootstrap ที่ใช้ทั้งกับแท็บและ badge ในตาราง */
  color: string;
  count: number;
}

@Component({
  selector: 'app-cpd-hours-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbPaginationModule,
    NgbTooltipModule,
    NgbAlertModule,
    NgbDropdownModule,
    StaffHeaderComponent,
    StaffNavComponent
  ],
  templateUrl: './cpd-hours-management.component.html',
  styleUrl: './cpd-hours-management.component.scss'
})
export class CpdHoursManagementComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  private userSubscription: Subscription | undefined;

  records: CpdHoursRecord[] = [];
  filteredRecords: CpdHoursRecord[] = [];
  isLoading = false;
  error: string | null = null;

  // Status lists
  cpdCourseStatusList: any[] = [];

  /**
   * แท็บแยกตามสถานะใบคำขอ สร้างจาก /cpd-course-status ที่โหลดมาจริง จึงไม่มี
   * การเพิ่มสถานะใหม่เองในหน้านี้ แท็บแรกคือ "ทั้งหมด" เสมอ
   */
  statusTabs: CpdStatusTab[] = [];

  /** ลำดับแท็บที่ต้องการให้แสดง สถานะนอกรายการนี้ต่อท้ายตามลำดับของ API */
  private readonly statusTabOrder = ['รอ Approve', 'Approved', 'ยกเลิกใบคำขอ', 'คำขอเก่า'];

  /** สีของแต่ละสถานะ ใช้ร่วมกันระหว่างแท็บกับ badge ในตาราง */
  private readonly statusColors: Record<string, string> = {
    'รอ Approve': 'warning',
    'Approved': 'success',
    'ยกเลิกใบคำขอ': 'danger',
    'คำขอเก่า': 'secondary'
  };
  cpdPaymentStatusList: any[] = [];

  // Filter properties
  filters: CpdFilters = {
    searchTerm: '',
    email: '',
    cid: '',
    courseTitle: '',
    isVerified: 'all',
    courseStatus: '',
    paymentStatus: '',
    yearFilter: '',
    dateFrom: '',
    dateTo: ''
  };

  // Pagination
  page = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50, 100];
  collectionSize = 0;

  // Sorting
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  private subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modalService: NgbModal,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    console.log('🔄 CpdHoursManagementComponent ngOnInit');

    // Subscribe to auth user
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      console.log('👤 Current user updated:', user);
      this.currentUser = user;
      this.cdr.detectChanges();
    });

    // Load status lists first
    this.loadCpdCourseStatusList();
    this.loadCpdPaymentStatusList();

    // Load data immediately
    console.log('📥 Loading CPD hours data immediately on init');
    this.loadCpdHours();
  }

  ngOnDestroy(): void {
    console.log('🗑️ CpdHoursManagementComponent ngOnDestroy');
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this.subscription.unsubscribe();
  }

  onLogout(): void {
    this.authService.logout();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();

    switch (route) {
      case 'users':
        this.router.navigate(['/staff/user-management']);
        break;
      case 'courses':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'reports':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'settings':
        this.router.navigate(['/staff/dashboard']);
        break;
      default:
        this.router.navigate(['/staff/dashboard']);
        break;
    }
  }

  /**
   * Load CPD Course Status list from API
   */
  loadCpdCourseStatusList(): void {
    const apiUrl = `${environment.apiUrl}/cpd-course-status`;
    console.log('📡 Loading CPD Course Status from:', apiUrl);

    this.http.get<any[]>(apiUrl).subscribe({
      next: (response) => {
        console.log('✅ CPD Course Status loaded:', response);
        this.cpdCourseStatusList = response || [];
        this.recomputeStatusTabs();
        this.cdr.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error loading CPD Course Status:', error);
        this.cpdCourseStatusList = [];
        this.recomputeStatusTabs();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Load CPD Payment Status list from API
   */
  loadCpdPaymentStatusList(): void {
    const apiUrl = `${environment.apiUrl}/cpd-payment-status`;
    console.log('📡 Loading CPD Payment Status from:', apiUrl);

    this.http.get<any[]>(apiUrl).subscribe({
      next: (response) => {
        console.log('✅ CPD Payment Status loaded:', response);
        this.cpdPaymentStatusList = response || [];
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error loading CPD Payment Status:', error);
        this.cpdPaymentStatusList = [];
      }
    });
  }

  /**
   * Load all CPD hours records from API
   */
  loadCpdHours(): void {
    console.log('🚀 Loading CPD hours from API...');
    console.log('📊 Current page:', this.page, 'Page size:', this.pageSize);

    this.isLoading = true;
    this.error = null;

    this.cdr.detectChanges();

    const apiUrl = `${environment.apiUrl}/cpd-hours`;
    console.log('📡 Fetching from:', apiUrl);

    this.http.get<CpdHoursRecord[]>(apiUrl).subscribe({
      next: (response) => {
        console.log('✅ API Response:', response);
        console.log('📦 Data count:', response?.length || 0);

        // โหลดมาทั้งหมด ไม่ตัดรายการปีเก่าทิ้งตั้งแต่ตอนโหลด มิฉะนั้นแท็บ "คำขอเก่า"
        // จะว่างเสมอ และ Export CSV จะไม่มีทางได้ข้อมูลย้อนหลัง
        // ถ้าต้องการดูเฉพาะปีใดปีหนึ่งให้ใช้ตัวกรอง "ปี" ที่มีอยู่แล้ว
        this.records = (response || [])
          // Sort by most recently updated first (default order on page load)
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        this.filteredRecords = [...this.records];
        this.collectionSize = this.filteredRecords.length;
        this.recomputeStatusTabs();
        this.isLoading = false;

        console.log('✨ Filtered records count:', this.filteredRecords.length);
        console.log('✨ Collection size:', this.collectionSize);

        this.cdr.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ API Error:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error details:', error.error);

        this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล: ${error.message}`;
        this.records = [];
        this.filteredRecords = [];
        this.collectionSize = 0;
        this.recomputeStatusTabs();
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Handle page change
   */
  onPageChange(newPage: number): void {
    console.log('📄 Page changed to:', newPage);
    this.page = newPage;
    this.cdr.detectChanges();
  }

  /**
   * Apply filters to record list (client-side filtering)
   */
  applyFilters(): void {
    console.log('🔍 Applying filters:', this.filters);

    if (!this.records || this.records.length === 0) {
      this.filteredRecords = [];
      this.collectionSize = 0;
      return;
    }

    try {
      this.filteredRecords = this.records.filter(record =>
        this.matchesNonStatusFilters(record) && this.matchesStatusFilter(record)
      );

      this.collectionSize = this.filteredRecords.length;
      this.page = 1; // Reset to first page when filtering
      this.recomputeStatusTabs();
      this.applySort(); // คงลำดับการเรียงที่ผู้ใช้เลือกไว้
      this.cdr.detectChanges();

    } catch (error) {
      console.error('❌ Error in applyFilters:', error);
      this.filteredRecords = [...this.records];
      this.collectionSize = this.filteredRecords.length;
      this.recomputeStatusTabs();
      this.cdr.detectChanges();
    }
  }

  /**
   * เงื่อนไขกรองทุกข้อ "ยกเว้น" สถานะใบคำขอ แยกออกมาเพื่อให้นับจำนวนในแต่ละแท็บได้
   * โดยยังเคารพตัวกรองอื่นที่ผู้ใช้เลือกไว้
   */
  private matchesNonStatusFilters(record: CpdHoursRecord): boolean {
    {
        // Search term
        if (this.filters.searchTerm) {
          const searchLower = this.filters.searchTerm.toLowerCase();
          const matchesSearch =
            record.user.email.toLowerCase().includes(searchLower) ||
            record.user.cid.includes(searchLower) ||
            record.course.title.toLowerCase().includes(searchLower) ||
            record.sessionTitle.toLowerCase().includes(searchLower);

          if (!matchesSearch) return false;
        }

        // Email
        if (this.filters.email && !record.user.email.toLowerCase().includes(this.filters.email.toLowerCase())) {
          return false;
        }

        // CID
        if (this.filters.cid && !record.user.cid.includes(this.filters.cid)) {
          return false;
        }

        // Course Title
        if (this.filters.courseTitle && !record.course.title.toLowerCase().includes(this.filters.courseTitle.toLowerCase())) {
          return false;
        }

        // Verification Status
        if (this.filters.isVerified !== 'all') {
          const isVerified = this.filters.isVerified === 'verified';
          if (record.isVerified !== isVerified) {
            return false;
          }
        }

        // Payment Status (เทียบด้วย cpdPaymentStatusId)
        if (this.filters.paymentStatus &&
            String(record.cpdPaymentStatusId ?? '') !== this.filters.paymentStatus) {
          return false;
        }

        // Year Filter
        if (this.filters.yearFilter && record.year.toString() !== this.filters.yearFilter) {
          return false;
        }

        // Date Range
        if (this.filters.dateFrom) {
          const recordDate = new Date(record.studyDate);
          const fromDate = new Date(this.filters.dateFrom);
          if (recordDate < fromDate) return false;
        }

        if (this.filters.dateTo) {
          const recordDate = new Date(record.studyDate);
          const toDate = new Date(this.filters.dateTo);
          if (recordDate > toDate) return false;
        }

        return true;
    }
  }

  // ── แท็บแยกสถานะใบคำขอ ──

  /** กรองตามแท็บสถานะที่เลือกอยู่ ('' = ทั้งหมด) */
  private matchesStatusFilter(record: CpdHoursRecord): boolean {
    if (!this.filters.courseStatus) return true;
    return String(record.cpdCourseStatusId ?? '') === this.filters.courseStatus;
  }

  /**
   * สร้างแท็บสถานะใหม่พร้อมจำนวนในแต่ละแท็บ
   * จำนวนนับจากรายการที่ผ่านตัวกรองอื่นแล้ว ผู้ใช้จึงเห็นล่วงหน้าว่ากดแท็บไหน
   * จะได้กี่รายการ
   */
  private recomputeStatusTabs(): void {
    const matches = (this.records || []).filter(record => this.matchesNonStatusFilters(record));

    this.statusTabs = [
      { id: '', name: 'ทั้งหมด', color: 'dark', count: matches.length },
      ...this.getOrderedStatusList().map(status => ({
        id: status.id,
        name: status.name,
        color: this.getStatusColor(status.name),
        count: matches.filter(record => record.cpdCourseStatusId === status.id).length
      }))
    ];
  }

  /** เรียงสถานะตาม statusTabOrder ส่วนที่ไม่อยู่ในรายการต่อท้ายตามลำดับของ API */
  private getOrderedStatusList(): Array<{ id: number; name: string }> {
    const list = [...(this.cpdCourseStatusList || [])];
    return list.sort((a, b) => {
      const ai = this.statusTabOrder.indexOf(a?.name);
      const bi = this.statusTabOrder.indexOf(b?.name);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }

  /** เลือกแท็บสถานะ */
  selectStatusTab(tab: CpdStatusTab): void {
    const nextStatus = tab.id === '' ? '' : String(tab.id);
    if (this.filters.courseStatus === nextStatus) return;

    this.filters.courseStatus = nextStatus;
    this.applyFilters();
  }

  isActiveStatusTab(tab: CpdStatusTab): boolean {
    const current = this.filters.courseStatus || '';
    return current === (tab.id === '' ? '' : String(tab.id));
  }

  private getStatusColor(statusName?: string): string {
    return this.statusColors[statusName || ''] || 'secondary';
  }

  /**
   * Get current page records
   */
  getCurrentPageRecords(): CpdHoursRecord[] {
    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredRecords.slice(startIndex, endIndex);
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      email: '',
      cid: '',
      courseTitle: '',
      isVerified: 'all',
      courseStatus: '',
      paymentStatus: '',
      yearFilter: '',
      dateFrom: '',
      dateTo: ''
    };
    this.page = 1;
    this.applyFilters();
  }

  /**
   * Sort records by field
   */
  /** วันและเวลาที่ผู้ใช้ยื่นคำขอเข้ามาในระบบ */
  formatDateTime(value?: string | null): string {
    if (!value) return '-';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '-';
    return `${date.toLocaleDateString('th-TH')} ${date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.`;
  }

  /** เลขที่สมาชิก */
  getMemberNo(record: CpdHoursRecord): string {
    return record?.user?.userInformation?.vatId || '-';
  }

  /** เลขที่ผู้ประเมินหลัก */
  getValuerNo(record: CpdHoursRecord): string {
    return record?.user?.userInformation?.valuerId || '-';
  }

  /** มีหลักฐานการโอนหรือยัง */
  hasPaymentSlip(record: CpdHoursRecord): boolean {
    return !!record?.paymentSlip && String(record.paymentSlip).trim() !== '';
  }

  openPaymentSlip(record: CpdHoursRecord, event: Event): void {
    event.stopPropagation();
    if (this.hasPaymentSlip(record)) {
      window.open(record.paymentSlip as string, '_blank');
    }
  }

  /** สถานะการออกใบเสร็จ */
  getReceiptStatusText(record: CpdHoursRecord): string {
    return record?.codeReceipt ? 'ออกใบเสร็จแล้ว' : 'รอดำเนินการ';
  }

  getReceiptStatusClass(record: CpdHoursRecord): string {
    return record?.codeReceipt ? 'bg-success' : 'bg-secondary';
  }

  isSortedBy(field: string): boolean {
    return this.sortField === field;
  }

  /** คอลัมน์ที่เรียงลำดับได้ */
  private readonly sortableFields = [
    'createdAt', 'email', 'fullName', 'memberNo', 'valuerNo', 'courseTitle',
    'hoursStudied', 'studyDate', 'price', 'paymentSlip', 'paymentStatus', 'receiptStatus'
  ];

  /** ค่าที่ใช้เรียงของแต่ละคอลัมน์ */
  private sortValue(record: CpdHoursRecord, field: string): string | number {
    switch (field) {
      case 'createdAt': return record.createdAt ? new Date(record.createdAt).getTime() : 0;
      case 'email': return (record.user?.email || '').toLowerCase();
      case 'fullName': return this.getFullName(record).toLowerCase();
      case 'memberNo': return this.getMemberNo(record);
      case 'valuerNo': return this.getValuerNo(record);
      case 'courseTitle': return (record.course?.title || '').toLowerCase();
      case 'hoursStudied': return Number(record.hoursStudied ?? 0) || 0;
      case 'studyDate': return record.studyDate ? new Date(record.studyDate).getTime() : 0;
      case 'price': return Number(record.course?.price ?? 0) || 0;
      case 'paymentSlip': return this.hasPaymentSlip(record) ? 1 : 0;
      case 'paymentStatus': return record.cpdPaymentStatus?.name || '';
      case 'receiptStatus': return record.codeReceipt ? 1 : 0;
      default: return '';
    }
  }

  /**
   * กดหัวคอลัมน์เพื่อเรียงลำดับ
   * ครั้งแรก = น้อยไปมาก (asc), ครั้งที่สอง = มากไปน้อย (desc), ครั้งที่สาม = ยกเลิกการเรียง
   */
  sortBy(field: string): void {
    if (!this.sortableFields.includes(field)) return;

    if (this.sortField !== field) {
      this.sortField = field;
      this.sortDirection = 'asc';
    } else if (this.sortDirection === 'asc') {
      this.sortDirection = 'desc';
    } else {
      this.sortField = '';
      this.sortDirection = 'asc';
    }

    this.page = 1;
    this.applySort();
    this.cdr.detectChanges();
  }

  private applySort(): void {
    if (!this.sortField) {
      this.filteredRecords.sort((a, b) =>
        new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
      return;
    }

    const dir = this.sortDirection === 'asc' ? 1 : -1;
    this.filteredRecords.sort((a, b) => {
      const va = this.sortValue(a, this.sortField);
      const vb = this.sortValue(b, this.sortField);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) return '⇅';
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  /**
   * Change page size
   */
  changePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
    this.cdr.detectChanges();
  }

  /**
   * Refresh data
   */
  refreshData(): void {
    console.log('🔄 Refreshing data...');
    this.loadCpdHours();
  }

  /**
   * Export to CSV
   */
  exportToCSV(): void {
    if (!this.filteredRecords || this.filteredRecords.length === 0) {
      alert('ไม่มีข้อมูลสำหรับ Export');
      return;
    }

    // เรียงตามหัวตาราง แล้วต่อท้ายด้วยข้อมูลที่ไม่ได้แสดงบนตาราง
    const headers = [
      'วันและเวลาที่ยื่นคำขอ',
      'อีเมล',
      'ชื่อ-นามสกุล',
      'เลขที่สมาชิก',
      'เลขที่ผู้ประเมินหลัก',
      'หัวข้อหลักสูตร',
      'จำนวน CPD (ชม.)',
      'วันที่อบรมสัมมนา',
      'ราคา (บาท)',
      'หลักฐานการโอน',
      'สถานะการชำระเงิน',
      'สถานะการออกใบเสร็จ',
      'หมายเหตุ',
      // ข้อมูลเพิ่มเติม (ไม่ได้แสดงบนตาราง)
      'เลขบัตรประชาชน',
      'เบอร์โทร',
      'หน่วยงาน',
      'สถานะการยืนยัน'
    ];

    const rows = this.filteredRecords.map(r => [
      this.formatDateTime(r.createdAt),
      r.user?.email || '-',
      this.getFullName(r),
      this.getMemberNo(r),
      this.getValuerNo(r),
      r.course?.title || '-',
      r.hoursStudied ?? '-',
      this.formatDate(r.studyDate),
      Number(r.course?.price || 0).toFixed(2),
      this.hasPaymentSlip(r) ? 'มีหลักฐาน' : 'ไม่มีหลักฐาน',
      this.getCpdPaymentStatusName(r),
      this.getReceiptStatusText(r),
      (r.notes || '-').replace(/[\r\n]+/g, ' '),
      r.user?.cid || '-',
      getPhoneNumberFrom(r.user?.userInformation),
      getOrganizationFrom(r.user?.userInformation),
      r.isVerified ? 'ยืนยันแล้ว' : 'รอยืนยัน'
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // เพิ่ม BOM เพื่อให้ Excel อ่านภาษาไทยได้ถูกต้อง
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `cpd_hours_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  /**
   * Get user's full Thai name (คำนำหน้า + ชื่อ + นามสกุล)
   */
  getFullName(record: CpdHoursRecord): string {
    const info = record.user?.userInformation;
    if (!info) return '-';

    const fullName = [info.titleName, info.firstNameThai, info.lastNameThai]
      .filter(part => part && part.trim())
      .join(' ')
      .trim();

    return fullName || '-';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH');
  }

  /**
   * Get CPD Course Status name by ID
   */
  getCpdCourseStatusName(record: CpdHoursRecord): string {
    // First try to get from the nested object if it exists
    if (record.cpdCourseStatus?.name) {
      return record.cpdCourseStatus.name;
    }

    // Otherwise look it up in the status list
    if (record.cpdCourseStatusId) {
      const status = this.cpdCourseStatusList.find(s => s.id === record.cpdCourseStatusId);
      return status ? status.name : '-';
    }

    return '-';
  }

  /**
   * Get CPD Payment Status name by ID
   */
  getCpdPaymentStatusName(record: CpdHoursRecord): string {
    // First try to get from the nested object if it exists
    if (record.cpdPaymentStatus?.name) {
      return record.cpdPaymentStatus.name;
    }

    // Otherwise look it up in the status list
    if (record.cpdPaymentStatusId) {
      const status = this.cpdPaymentStatusList.find(s => s.id === record.cpdPaymentStatusId);
      return status ? status.name : '-';
    }

    return '-';
  }

  /**
   * Get CPD Course Status badge class
   */
  getCpdCourseStatusBadgeClass(record: CpdHoursRecord): string {
    const statusName = this.getCpdCourseStatusName(record);

    // Map status names to badge classes
    switch (statusName) {
      case 'ยืนยันแล้ว':
      case 'อนุมัติ':
        return 'bg-success';
      case 'รอยืนยัน':
      case 'รอการอนุมัติ':
        return 'bg-warning';
      case 'ไม่อนุมัติ':
      case 'ยกเลิก':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get CPD Payment Status badge class
   */
  getCpdPaymentStatusBadgeClass(record: CpdHoursRecord): string {
    const statusName = this.getCpdPaymentStatusName(record);

    // Map status names to badge classes
    switch (statusName) {
      case 'ชำระเงินแล้ว':
      case 'ชำระแล้ว':
        return 'bg-success';
      case 'รอชำระเงิน':
      case 'ยังไม่ได้ชำระเงิน':
        return 'bg-warning';
      case 'รอตรวจสอบ':
        return 'bg-info';
      case 'ยกเลิก':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get verification badge class
   */
  getVerificationBadgeClass(isVerified: boolean): string {
    return isVerified ? 'bg-success' : 'bg-warning';
  }

  /**
   * Get verification text
   */
  getVerificationText(isVerified: boolean): string {
    return isVerified ? 'ยืนยันแล้ว' : 'รอยืนยัน';
  }

  /**
   * Get payment status badge class
   */
  getPaymentStatusBadgeClass(status: string): string {
    switch (status) {
      case 'ชำระเงินแล้ว':
        return 'bg-success';
      case 'ยังไม่ได้ชำระเงิน':
        return 'bg-warning';
      case 'รอตรวจสอบ':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get level badge class
   */
  getLevelBadgeClass(level: string): string {
    switch (level) {
      case 'beginner':
        return 'bg-info';
      case 'intermediate':
        return 'bg-primary';
      case 'advanced':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get level text
   */
  getLevelText(level: string): string {
    switch (level) {
      case 'beginner':
        return 'เริ่มต้น';
      case 'intermediate':
        return 'กลาง';
      case 'advanced':
        return 'สูง';
      default:
        return level;
    }
  }

  /**
   * View record details
   */
  viewRecord(record: CpdHoursRecord): void {
    console.log('👁️ View record:', record);
    // Navigate to CPD course detail page
    this.router.navigate(['/staff/cpd-hours-management/detail', record.id]);
  }

  /**
   * Verify record
   */
  verifyRecord(record: CpdHoursRecord): void {
    console.log('✅ Verify record:', record);
    const confirmation = confirm(`คุณต้องการยืนยัน CPD Hours สำหรับ ${record.sessionTitle} หรือไม่?`);

    if (confirmation) {
      const apiUrl = `${environment.apiUrl}/cpd-hours/${record.id}`;
      console.log('📡 Sending PATCH request to:', apiUrl);

      this.http.patch(apiUrl, { isVerified: true }).subscribe({
        next: (response) => {
          console.log('✅ API Response:', response);
          record.isVerified = true;
          this.cdr.detectChanges();
          alert(`✅ ยืนยัน CPD Hours เรียบร้อยแล้ว`);
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ API Error:', error);
          alert(`เกิดข้อผิดพลาดในการยืนยัน: ${error.message}`);
        }
      });
    }
  }

  /**
   * Unverify record
   */
  unverifyRecord(record: CpdHoursRecord): void {
    console.log('❌ Unverify record:', record);
    const confirmation = confirm(`คุณต้องการยกเลิกการยืนยัน CPD Hours สำหรับ ${record.sessionTitle} หรือไม่?`);

    if (confirmation) {
      const apiUrl = `${environment.apiUrl}/cpd-hours/${record.id}`;
      console.log('📡 Sending PATCH request to:', apiUrl);

      this.http.patch(apiUrl, { isVerified: false }).subscribe({
        next: (response) => {
          console.log('✅ API Response:', response);
          record.isVerified = false;
          this.cdr.detectChanges();
          alert(`❌ ยกเลิกการยืนยัน CPD Hours เรียบร้อยแล้ว`);
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ API Error:', error);
          alert(`เกิดข้อผิดพลาดในการยกเลิกการยืนยัน: ${error.message}`);
        }
      });
    }
  }

  /**
   * Edit record
   */
  editRecord(record: CpdHoursRecord): void {
    console.log('🔧 Edit record:', record);
    // TODO: Implement edit record modal
    alert(`แก้ไข CPD Hours: ${record.id}`);
  }

  /**
   * Delete record
   */
  deleteRecord(record: CpdHoursRecord): void {
    console.log('🗑️ Delete record:', record);
    const confirmation = confirm(`คุณต้องการลบ CPD Hours สำหรับ ${record.sessionTitle} หรือไม่?\n\nการดำเนินการนี้ไม่สามารถยกเลิกได้!`);

    if (confirmation) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index > -1) {
        this.records.splice(index, 1);
        this.applyFilters();
        alert(`✅ ลบ CPD Hours เรียบร้อยแล้ว`);
      }
    }
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByRecordId(index: number, record: CpdHoursRecord): string {
    return record.id;
  }

  /**
   * Math helper for template
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  /**
   * Handle dropdown open/close state
   */
  onDropdownOpenChange(isOpen: boolean, recordId: string): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const dropdownButton = document.getElementById(`dropdownMenuButton-${recordId}`);
        const tableRow = dropdownButton?.closest('tr');
        const tableContainer = dropdownButton?.closest('.table-responsive');

        if (tableRow) {
          if (isOpen) {
            tableRow.classList.add('dropdown-active');
            tableRow.style.position = 'relative';
            tableRow.style.zIndex = '1050';

            if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            }

            const rowRect = tableRow.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;

            if (dropdownMenu && rowRect.bottom > viewportHeight - 200) {
              dropdownMenu.style.top = 'auto';
              dropdownMenu.style.bottom = '100%';
              dropdownMenu.style.marginBottom = '2px';
              dropdownMenu.style.marginTop = '0';
            }
          } else {
            tableRow.classList.remove('dropdown-active');
            tableRow.style.position = '';
            tableRow.style.zIndex = '';

            if (tableContainer && window.innerWidth >= 992) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            } else if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = '';
            }

            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;
            if (dropdownMenu) {
              dropdownMenu.style.top = '';
              dropdownMenu.style.bottom = '';
              dropdownMenu.style.marginBottom = '';
              dropdownMenu.style.marginTop = '';
            }
          }
        }
      }, 0);
    });
  }
}
